import type { Property } from '~/core/entities/property';
import type { PaginatedPropertyFilters } from '~/core/entities/property/filters';
import { Result, type ResultTuple } from '~~/core/helpers/result';
import type { NuxtAddressesApiReturn } from '~~/server/api/properties/addresses.get';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/properties/index.get';

interface NuxtPropertiesRepository {
	list(
		filters: Partial<PaginatedPropertyFilters>
	): Promise<ResultTuple<NuxtListPropertiesApiReturn>>;
	getOne(identifier: string): Promise<ResultTuple<Property>>;
	checkOne(identifier: string): Promise<
		ResultTuple<{
			commercialId: string;
			slug: Property['slug'];
			publicationStatus: Property['publicationStatus'];
		} | null>
	>;
	getAddresses(): Promise<ResultTuple<NuxtAddressesApiReturn | ''>>;
	getSimilarProperties(params: {
		identifier: string;
		propertyType?: PaginatedPropertyFilters['propertyTypes'];
		region?: string;
	}): Promise<ResultTuple<Property[]>>;
	getChosenForYouProperties(params: {
		identifier: string;
		regions?: string[];
		askingPrice?: number;
	}): Promise<ResultTuple<Property[]>>;
}

export const nuxtPropertiesRepository: NuxtPropertiesRepository = {
	async list(filters) {
		try {
			const response = await $fetch('/api/properties', {
				method: 'POST',
				body: filters
			});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching properties from the API.',
				source: 'nuxtPropertiesRepository.list',
				originalErrorObject: error as Error
			});
		}
	},
	async getOne(identifier) {
		try {
			const response = await $fetch<Property>(`/api/properties/${identifier}`, {
				method: 'GET'
			});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: `Error while fetching property with identifier ${identifier} from the API.`,
				source: 'nuxtPropertiesRepository.getOne',
				originalErrorObject: error as Error
			});
		}
	},
	async checkOne(identifier) {
		try {
			const response = await $fetch<{
				commercialId: string;
				slug: Property['slug'];
				publicationStatus: Property['publicationStatus'];
			} | null>(`/api/properties/${identifier}/check`, {
				method: 'GET'
			});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: `Error while fetching property with identifier ${identifier} from the API.`,
				source: 'nuxtPropertiesRepository.getOne',
				originalErrorObject: error as Error
			});
		}
	},
	async getSimilarProperties({ identifier, propertyType, region }) {
		const [similarProperties, similarPropertiesError] = await $fetch<
			ResultTuple<Property[]>
		>(`/api/properties/${identifier}/similar-properties`, {
			query: { propertyType: propertyType, region }
		});

		if (!similarProperties || similarPropertiesError) {
			return Result.error({
				message: `Error while fetching similar properties from the API.`,
				source: 'nuxtPropertiesRepository.getSimilarProperties',
				originalErrorObject: {
					...similarPropertiesError,
					name: 'similarPropertiesError'
				}
			});
		}

		return Result.ok(similarProperties);
	},
	async getChosenForYouProperties({ identifier, regions, askingPrice }) {
		const [chosenForYouProperties, chosenForYouPropertiesError] = await $fetch<
			ResultTuple<Property[]>
		>(`/api/properties/${identifier}/chosen-for-you`, {
			query: { regions, askingPrice }
		});

		if (!chosenForYouProperties || chosenForYouPropertiesError) {
			return Result.error({
				message: `Error while fetching chosen for you properties from the API.`,
				source: 'nuxtPropertiesRepository.getChosenForYouProperties',
				originalErrorObject: {
					...chosenForYouPropertiesError,
					name: 'chosenForYouPropertiesError'
				} as Error
			});
		}

		return Result.ok(chosenForYouProperties);
	},
	async getAddresses() {
		try {
			const response = await $fetch(`/api/properties/addresses`, {
				method: 'GET'
			});

			if (!response)
				return Result.error({
					message: `Error while fetching properties addresses from the API.`,
					source: 'nuxtPropertiesRepository.getAddresses'
				});

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: `Error while fetching properties addresses from the API.`,
				source: 'nuxtPropertiesRepository.getAddresses',
				originalErrorObject: error as Error
			});
		}
	},
};
